import Grid2 from "@mui/material/Unstable_Grid2";
import {Collapse, styled, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {monthNames} from "../../helpers/DateHelpers";
import useOrderState from "../../stores/orderStore";
import PriceList from "../../types/PriceList";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({theme}) => ({
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(.5),
    border: 0,
    [theme.breakpoints.up("sm")]: {
      height: 44
    },
    '&.Mui-disabled': {
      border: 0,
    },
    '&.MuiButtonBase-root': {
      borderRadius: 0,
      backgroundColor: theme.palette.stepper.main,
      fontSize: 18,
      fontWeight: 500,
      color: theme.palette.primary.main,
    },
    '&.MuiButtonBase-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    },
    '&:first-of-type': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
      marginLeft: 0
    },
    '&:last-of-type': {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      marginRight: 0
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)(({theme}) => ({
  '&.MuiToggleButton-root.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  }
}));


type props = {
  selectedMonths: number[]
  setSelectedMonths: (months: number[]) => void
  selectedAmount: number
  setSelectedAmount: (amount: number) => void
  modifyLater?: boolean
  hasExistingMonths?: boolean
  title?: string
  prices?: PriceList | null
  existingCustomer?: boolean
}

export default function PaymentFreeMonth3({selectedMonths, setSelectedMonths, selectedAmount, setSelectedAmount, modifyLater, hasExistingMonths, prices, title = "Betalingsfri måned", existingCustomer = false}: props) {
  const state = useOrderState((state) => state)

  function selectMonth(idx: number) {
    if (selectedAmount === 1)
      setSelectedMonths([idx])
    else if (selectedMonths.includes(idx))
      setSelectedMonths(selectedMonths.filter(m => m !== idx))
    else if (selectedMonths.length < selectedAmount)
      setSelectedMonths([...selectedMonths, idx])
  }

  function selectAmount(amount: number) {
    if (amount === null)
      return
    setSelectedAmount(amount)
    if (amount === 0)
      setSelectedMonths([])
    if (amount === 1 && selectedMonths.length === 2)
      setSelectedMonths([selectedMonths[0]])
  }

  function monthIsDisabled(month: number) {
    if (monthIsCurrentOrNext(month))
      return true

    if (selectedAmount === 2) {
      const gt = month + 1 === 13 ? 1 : month + 1
      const lt = month - 1 === 0 ? 12 : month - 1
      return (selectedMonths.length === 2
          && !selectedMonths.includes(month))
        // and month cannot be one lower or greater than a selected month
        || selectedMonths.includes(lt)
        || selectedMonths.includes(gt)
    }
    return false
  }

  function monthIsCurrentOrNext(month: number) {
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth() + 1
    const nextMonth = new Date(new Date().setMonth(currentDate.getMonth() + 1)).getMonth() + 1

    if(existingCustomer)
      return month == currentMonth

    return month === currentMonth
      || month === nextMonth
  }

  function getMonthButtons() {
    return monthNames.map((month, idx) => <Grid2 key={idx} xs={4} sm={2} lg={2}>
      <StyledToggleButton key={idx} value={idx + 1} fullWidth color="primary" sx={theme => ({color: theme.palette.primary.main})}
                          selected={selectedMonths.includes(idx + 1)}
                          disabled={monthIsDisabled(idx + 1)}
                          onClick={() => selectMonth(idx + 1)}>
        {month}
      </StyledToggleButton>
    </Grid2>)
  }

  return (
    <>
      <Grid2 xs={12}>
        <Typography variant="overline" fontSize={14} fontWeight="700">{title}</Typography> <br/>
        <StyledToggleButtonGroup value={selectedAmount} onChange={(e, v) => selectAmount(v)} exclusive fullWidth>
          {(!modifyLater || (state.paymentFree?.paymentFreeMonths?.length ?? 0) > 0) &&
            <ToggleButton value={0}>
              Ingen {prices && `(${prices.none} kr)`}
            </ToggleButton>
          }
          <ToggleButton value={1}>
            1 måned {prices && `(${prices.single} kr)`}
          </ToggleButton>
          <ToggleButton value={2}>
            2 måneder {prices && `(${prices.double} kr)`}
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Grid2>
      <Grid2 xs={12}>
        <Collapse in={selectedAmount > 0}>
          <Grid2 container padding={0} columnSpacing={1} rowSpacing={4} mb={1} mt={0}>
            <Grid2 xs={12}>
              <Typography>Velg når du vil ha betalingsfri. Du kan ikke starte med en betalingsfri måned, ikke velge to måneder etter hverandre og ikke velge mer enn to måneder i året. Dersom måneden har passert vil den gjelde fra neste år.</Typography>
            </Grid2>
            {getMonthButtons()}
          </Grid2>
        </Collapse>
      </Grid2>
    </>
  )
}